import {useState, useEffect} from 'react'
import compose from 'recompose/compose'
import {graphql, useMutation} from 'react-apollo'
import GET_DISTRICTS from '@/graphql/queries/districts'
import EDIT_PARTNERBROKER_PROFILE from '@/graphql/mutations/editPartnerBrokerProfile'
import withUserProfile from '@/lib/hoc/withUserProfile'
import withRestoredScroll from '@/lib/hoc/withRestoredScroll'
import logger from '@/lib/logger'
import {getUTMObject} from '@/lib/utm'
import {addInternationalCode, removeInternationalCode} from '@/lib/format'
import {
  validateName,
  validatePhone,
  validateEmail,
  validateCPF,
  validateCRECI,
  validateCity
} from '@/lib/validation'
import {USER_TYPE_PARTNER} from '@/lib/user'
import {getSha256Data} from '@/lib/user'
import Shell from '@/components/layout/templates/Default'
import Wrapper from '@/pages/partnerBroker/Wrapper'
import HeadHelmet from '@/components/shared/HeadHelmet'
import StepByStep from '@/components/shared/StepByStep'
import Result from '@/components/shared/Result'
import StepProfile from './steps/Profile'
import StepDistricts from './steps/Districts'
import {headHelmetProps, loggerAction, INITIAL_STEP} from './constants'
import resultStyles from '@/styles/molecules/Result/styles.module.css'

const PartnerBrokerCreate = ({districts, userProfile}) => {
  const [step, changeStep] = useState(INITIAL_STEP)
  const [stepStates, changeStepStates] = useState({
    profile: {
      name: undefined,
      phone: undefined,
      email: undefined,
      creci: undefined,
      cpf: undefined,
      city: undefined,
      terms: undefined
    },
    districts: []
  })
  const [result, changeResult] = useState({
    error: true,
    icon: '/img/icons/exclamation-mark.svg',
    title: 'Ocorreu um erro',
    text: 'Tente enviar os dados novamente',
    buttons: [
      {
        active: true,
        label: 'Tentar novamente',
        onClick: () => changeStep(INITIAL_STEP)
      }
    ]
  })

  const [editPartnerBrokerProfile, {data, error}] = useMutation(
    EDIT_PARTNERBROKER_PROFILE
  )

  const isStepProfileValid = () => {
    const {profile} = stepStates
    let isValid =
      validateName(profile.name) &&
      validatePhone(profile.phone) &&
      validateEmail(profile.email) &&
      validateCPF(profile.cpf) &&
      validateCRECI(profile.creci) &&
      validateCity(profile.city) &&
      profile.terms
    return isValid
  }

  const isStepDistrictsValid = () => stepStates.districts.length

  const isBackButtonDisabled = () => !(steps[step] && steps[step].stepBack)
  const isFwdButtonDisabled = () =>
    (step === 'profile' && !isStepProfileValid()) ||
    (step === 'districts' && !isStepDistrictsValid())

  const getLogStates = () => {
    return {
      step,
      ...stepStates,
      userProfile,
      utm: getUTMObject()
    }
  }

  const getUserProfileProps = () => {
    return {
      name: userProfile.name || stepStates.profile.name,
      email: userProfile.email || stepStates.profile.email,
      phone: userProfile.phone || stepStates.profile.phone,
      cpf: userProfile.cpf || stepStates.profile.cpf,
      creci: userProfile.creci || stepStates.profile.creci
    }
  }

  const getStepProfileState = () => {
    return {
      ...stepStates.profile,
      phone: removeInternationalCode(stepStates.profile.phone)
    }
  }

  const getSuccessResult = () => {
    return {
      success: true,
      icon: '/img/illustrations/check.svg',
      iconFull: true,
      title: 'Credenciamento concluído!',
      text:
        'Receba 15% da nossa comissão sempre que um imóvel que você indicou for vendido pela nossa equipe. A EmCasa negocia com o proprietário e anuncia o imóvel.',
      buttons: [
        {
          active: true,
          label: 'Indique um imóvel',
          to: '/corretor-parceiro/indicar',
          onClick: () =>
            logger.action(loggerAction.successbutton, getLogStates())
        }
      ]
    }
  }

  function onStepProfileChange(value) {
    changeStepStates({
      ...stepStates,
      profile: {
        ...stepStates.profile,
        ...value,
        phone: value.phone
          ? addInternationalCode(value.phone)
          : stepStates.profile.phone
      }
    })
  }

  function onStepDistrictsChange(value) {
    logger.action(loggerAction.onstepdistrictschange, {
      userProfile,
      selectedDistricts: value,
      ...getLogStates()
    })
    changeStepStates({
      ...stepStates,
      districts: value
    })
  }

  function onSubmit() {
    editPartnerBrokerProfile({
      variables: {
        id: userProfile.id,
        name: stepStates.profile.name,
        phone: stepStates.profile.phone,
        email: stepStates.profile.email,
        cpf: stepStates.profile.cpf,
        creci: stepStates.profile.creci,
        type: USER_TYPE_PARTNER,
        districts: stepStates.districts,
        utm: getUTMObject()
      }
    })
  }

  function onSuccess() {
    changeResult(getSuccessResult())
    changeStep('success')
    if (userProfile && (userProfile.phone || userProfile.email)) {
      const user = getSha256Data(userProfile)
      logger.action(loggerAction.success, {...getLogStates(), ...user})
    } else {
      logger.action(loggerAction.success, getLogStates())
    }
  }

  function onError() {
    changeResult({
      error: true,
      icon: '/img/icons/exclamation-mark.svg',
      title: 'Ocorreu um erro',
      text: 'Tente enviar os dados novamente',
      buttons: [
        {
          active: true,
          label: 'Tentar novamente',
          onClick: () => {
            logger.action(loggerAction.errorbutton)
            changeStep(INITIAL_STEP)
          }
        }
      ]
    })
    changeStep('error')
    logger.action(loggerAction.error, getLogStates())
  }

  function handleBackButtonClick() {
    logger.action(loggerAction.neighborhoodsback, getLogStates())
    logger.action(loggerAction.stepback, getLogStates())
  }

  function handleFwdButtonClick() {
    logger.action(
      step === 'profile'
        ? loggerAction.profilenext
        : loggerAction.neighborhoodsnext,
      getLogStates()
    )

    logger.action(loggerAction.stepforward, getLogStates())
  }

  function handleLastFwdClick() {
    changeStep('send')
  }

  const steps = {
    profile: {
      label: 'Perfil e Contato',
      component: StepProfile,
      stepBack: null,
      stepFwd: 'districts',
      stepProps: {
        state: getStepProfileState(),
        districts,
        userProfile,
        onChange: onStepProfileChange,
        onSubmit: () => !isFwdButtonDisabled() && changeStep('districts'),
        getLogStates: getLogStates,
        loggerAction: {
          onload: loggerAction.onloadprofile,
          oninputchange: loggerAction.oninputchange,
          profilemodalopen: loggerAction.profilemodalopen,
          profilemodallink: loggerAction.profilemodallink
        }
      }
    },
    districts: {
      label: 'Área de atuação',
      component: StepDistricts,
      stepBack: 'profile',
      stepFwd: 'send',
      stepProps: {
        state: stepStates,
        districts,
        userProfile,
        onChange: onStepDistrictsChange,
        loggerAction: {
          onload: loggerAction.onloaddistricts
        }
      }
    }
  }

  useEffect(() => {
    if (userProfile && userProfile.id) {
      changeStepStates({
        ...stepStates,
        profile: {
          ...stepStates.profile,
          ...getUserProfileProps()
        }
      })
    }
  }, [userProfile])

  useEffect(() => {
    if (step === 'send') {
      logger.action(loggerAction.send, getLogStates())
      onSubmit()
    }
  }, [step])

  useEffect(() => {
    if (data && data.changeEmail && data.editUserProfile) {
      logger.action(loggerAction.sendsuccess, {
        data,
        ...getLogStates()
      })
      onSuccess()
    }
  }, [data])

  useEffect(() => {
    if (error) {
      logger.capture(error)
      logger.action(loggerAction.senderror, {
        error,
        ...getLogStates()
      })
      onError()
    }
  }, [error])

  useEffect(() => logger.action(loggerAction.onload, getLogStates()), [])

  return (
    <Shell noFooter>
      <HeadHelmet {...headHelmetProps} />
      <Wrapper
        userProfile={userProfile}
        userType={USER_TYPE_PARTNER}
        partnerContent={
          <div className={resultStyles.ecResult__wrapper}>
            <Result {...getSuccessResult()} />
          </div>
        }
        content={
          <StepByStep
            steps={steps}
            step={step}
            result={result}
            navigateToStep={changeStep}
            backButtonLabel="Voltar"
            fwdButtonLabel={step === 'districts' ? 'Enviar' : 'Avançar'}
            isBackButtonDisabled={isBackButtonDisabled()}
            isFwdButtonDisabled={isFwdButtonDisabled()}
            onBackButtonClick={handleBackButtonClick}
            onFwdButtonClick={handleFwdButtonClick}
            onHandleLastFwdClick={handleLastFwdClick}
          />
        }
      />
    </Shell>
  )
}

export default compose(
  withUserProfile,
  graphql(GET_DISTRICTS, {
    props: ({data}) => ({
      districts: (data.districts || []).reduce((allCities, district) => {
        if (district.citySlug in allCities) {
          const districtsList = allCities[district.citySlug].districts

          districtsList.push(district)
          districtsList.sort((a, b) => (a.nameSlug > b.nameSlug) * 2 - 1)
          allCities[district.citySlug].districts = districtsList
        } else {
          allCities[district.citySlug] = {
            state: district.state,
            city: district.city,
            citySlug: district.citySlug,
            districts: [district]
          }
        }
        return allCities
      }, {})
    }),
    options: {
      fetchPolicy: 'cache-first'
    }
  }),
  withRestoredScroll
)(PartnerBrokerCreate)
